/**
 * Comprehensive Tool Directory Data Model & Repository for Lashary
 * Facebook Marketplace Tools, Automation Software & Free Seller Resources Hub
 */

export type ToolStatus = 'AVAILABLE' | 'UPDATED' | 'ARCHIVED' | 'UNAVAILABLE' | 'OFFICIAL_LINK_ONLY';
export type ToolDownloadType = 'DIRECT_DOWNLOAD' | 'OFFICIAL_LINK_ONLY' | 'WEB_BASED';

export interface ToolFaqItem {
  question: string;
  answer: string;
}

export interface ToolItem {
  name: string;
  slug: string;
  description: string;
  shortDescription: string;
  category: string;
  platform: string;
  os: string;
  version: string;
  releaseDate: string;
  updatedDate: string;
  developer: string;
  publisher: string;
  license: string;
  fileSize: string;
  officialWebsite: string;
  downloadType: ToolDownloadType;
  downloadURL: string;
  sourceURL?: string;
  screenshots: string[];
  systemRequirements: string[];
  installationInstructions: string[];
  changelog: string[];
  features: string[];
  limitations: string[];
  faq: ToolFaqItem[];
  seoTitle: string;
  metaDescription: string;
  canonicalURL: string;
  ogImage: string;
  schemaData?: Record<string, unknown>;
  status: ToolStatus;
  isFlagship?: boolean;
}

export const TOOLS_DATA: ToolItem[] = [
  {
    name: 'Lashary FB Market Suite',
    slug: 'lashary-fb-market-suite',
    shortDescription: 'The flagship Windows desktop automation workstation for high-volume Facebook Marketplace merchants, vehicle dealerships, and real estate agencies.',
    description: 'Lashary FB Market Suite v1.5 is a professional 64-bit Windows desktop workstation engineered to eliminate 4+ hours of daily copy-pasting for high-volume Facebook Marketplace merchants. Features 1-click batch publishing, 2-phase private draft safety, automatic phone number verification, AI-assisted buyer replies (Gemini 2.5 Flash), AI image generation, and Windows SAPI text-to-speech spoken alerts. Operates 100% locally with hardware-backed encryption and resilient local workstation storage.',
    category: 'Desktop Automation Software',
    platform: 'Windows Desktop Workstation',
    os: 'Windows 10 / Windows 11 (64-Bit Strictly Required)',
    version: '1.5.0',
    releaseDate: 'September 2026',
    updatedDate: 'September 2026',
    developer: 'Abdul Mueed Lashary & Aswad Ammar Lashary',
    publisher: 'Lashary Softwares',
    license: 'Commercial Proprietary (PKR 1,600 / $15 USD Monthly)',
    fileSize: '84.2 MB',
    officialWebsite: 'https://lasharyfbmarketsuite.pages.dev/tools/lashary-fb-market-suite',
    downloadType: 'DIRECT_DOWNLOAD',
    downloadURL: '/downloads/Lashary-FB-Market-Suite-1.5.exe',
    sourceURL: 'https://lasharyfbmarketsuite.pages.dev/download',
    screenshots: [
      '/screenshots/dashboard_dark.png',
      '/screenshots/vehicle_dark.png',
      '/screenshots/property_dark.png',
      '/screenshots/verification_dark.png',
      '/screenshots/01_overview.png'
    ],
    systemRequirements: [
      'Operating System: Windows 10 or Windows 11 (64-Bit strictly required)',
      'Web Browser: Google Chrome v115+ or Microsoft Edge installed',
      'System Memory: 4 GB RAM minimum (8 GB RAM recommended for multi-account sandboxing)',
      'Disk Space: 500 MB free SSD storage space',
      'Network: Stable residential broadband or 4G mobile hotspot connection'
    ],
    installationInstructions: [
      'Download the official 64-bit setup installer (Lashary-FB-Market-Suite-1.5.exe) directly to your PC.',
      'Run the installer. If Windows SmartScreen displays an unknown publisher prompt, click "More Info" and select "Run Anyway".',
      'Select your local installation directory and allow the setup wizard to complete.',
      'Launch Lashary FB Market Suite from your desktop shortcut and activate your operator license via WhatsApp concierge.'
    ],
    changelog: [
      'v1.5.0 Flagship Release: Specialized Vehicle & Property workspaces with custom attribute taxonomy.',
      'v1.5.0: Automatic Facebook phone number verification workflow automation.',
      'v1.5.0: Integrated Gemini 2.5 Flash for context-aware customer response drafting.',
      'v1.5.0: Native Windows SAPI text-to-speech engine announcing queue milestones and checkpoints.',
      'v1.5.0: Enhanced local data engine for atomic zero-loss listing state recovery.'
    ],
    features: [
      'Facebook Marketplace 1-Click Batch Automation with humanized jitter pacing',
      'Automatic Facebook Phone Number Verification during session initialization',
      'Specialized Listing Workspaces for Automotive Dealerships, Real Estate, and Retail Goods',
      '2-Phase Safe Draft Staging for thorough isolated preview before publishing',
      'AI-Powered Customer Reply drafting backed by Gemini intelligence',
      'In-Suite AI Image Generation and badge enhancement tools',
      'Native Windows SAPI Spoken Audio voice notifications',
      'Deterministic Unique-First Photo Allocator preventing duplicate image suppression',
      '100% Local PC Execution with hardware-backed security and complete privacy'
    ],
    limitations: [
      'Requires a 64-bit Windows operating system (macOS and Linux are not natively supported)',
      'Requires Google Chrome or Microsoft Edge installed on the host computer',
      'Automates browser interactions locally; does not operate as an unattended background cloud SaaS daemon',
      'Users must comply with Meta Platform terms of service and local commerce guidelines'
    ],
    faq: [
      {
        question: 'Does Lashary FB Market Suite run in the cloud or on my PC?',
        answer: 'Lashary FB Market Suite runs 100% locally on your Windows PC. Your account passwords, session cookies, and inventory catalogs never leave your machine.'
      },
      {
        question: 'How does 2-phase draft staging protect my account?',
        answer: 'Listings are staged to Facebook private drafts first. The suite allows you to review and verify all attributes in isolation before publishing with randomized human delay pacing.'
      },
      {
        question: 'How do I obtain an activation license?',
        answer: 'License activation is handled directly via WhatsApp with the software founders, Abdul Mueed Lashary (+92 334 788 1924) and Aswad Ammar Lashary (+92 371 043 4552).'
      }
    ],
    seoTitle: 'Lashary FB Market Suite v1.5 — Facebook Marketplace Desktop Automation',
    metaDescription: 'Official Windows desktop software for high-volume Facebook Marketplace sellers. 1-click batch publishing, automatic number verification, AI replies, and voice alerts.',
    canonicalURL: 'https://lasharyfbmarketsuite.pages.dev/tools/lashary-fb-market-suite',
    ogImage: 'https://lasharyfbmarketsuite.pages.dev/screenshots/01_overview.png',
    status: 'AVAILABLE',
    isFlagship: true,
  },
  {
    name: 'Facebook Marketplace Fee & Margin Calculator',
    slug: 'facebook-marketplace-fee-calculator',
    shortDescription: 'Free interactive financial calculator to determine net seller payouts, shipping deduction impacts, and profit margins on Marketplace transactions.',
    description: 'A dedicated, free online calculator for Facebook Marketplace sellers. Calculates platform transaction fees (including standard selling fees, prepaid shipping labels, and promotional boosts), determining your break-even pricing, gross margin percentage, and net cash payout.',
    category: 'Seller Profitability & Finance',
    platform: 'Web Application',
    os: 'Cross-Platform (Any modern browser)',
    version: '1.2.0',
    releaseDate: 'August 2026',
    updatedDate: 'September 2026',
    developer: 'Lashary Engineering Team',
    publisher: 'Lashary Softwares',
    license: 'Free Public Web Utility',
    fileSize: '0 MB (Runs in Browser)',
    officialWebsite: 'https://lasharyfbmarketsuite.pages.dev/tools/facebook-marketplace-fee-calculator',
    downloadType: 'WEB_BASED',
    downloadURL: '/tools/facebook-marketplace-fee-calculator',
    screenshots: ['/screenshots/01_overview.png'],
    systemRequirements: [
      'Modern web browser (Google Chrome, Mozilla Firefox, Safari, Microsoft Edge, Brave)',
      'Active internet connection'
    ],
    installationInstructions: [
      'No installation required. Access directly through your browser on desktop, tablet, or smartphone.'
    ],
    changelog: [
      'v1.2.0: Added shipping rate tier selection for USPS and UPS prepaid marketplace options.',
      'v1.1.0: Real-time net margin recalculation with instant elasticity curve graph.',
      'v1.0.0: Initial public release of the free seller profit estimator.'
    ],
    features: [
      'Instant net margin and net profit calculation',
      'Configurable selling price, item acquisition cost, and packaging expenses',
      'Prepaid shipping deduction calculator',
      'Currency toggle (PKR, USD, GBP, EUR, AED)',
      '1-Click CSV profit scenario export'
    ],
    limitations: [
      'Calculations are estimates based on standard platform fee schedules and do not account for individual regional sales taxes or custom merchant agreements'
    ],
    faq: [
      {
        question: 'Does Facebook charge a fee for local pickup listings?',
        answer: 'No. Local pickup sales on Facebook Marketplace generally incur 0% transaction fees from Facebook when buyers pay with cash or direct peer-to-peer transfer.'
      },
      {
        question: 'What is the fee for shipped items on Facebook Marketplace?',
        answer: 'For shipped listings processed through Facebook on-platform checkout, standard selling fees apply (typically 5% per transaction or a flat fee for items under $8.00 in the US).'
      }
    ],
    seoTitle: 'Facebook Marketplace Fee & Margin Calculator (Free 2026 Tool) — Lashary',
    metaDescription: 'Free Facebook Marketplace fee and seller margin calculator. Compute net payouts, platform fees, shipping expenses, and break-even pricing accurately.',
    canonicalURL: 'https://lasharyfbmarketsuite.pages.dev/tools/facebook-marketplace-fee-calculator',
    ogImage: 'https://lasharyfbmarketsuite.pages.dev/screenshots/01_overview.png',
    status: 'AVAILABLE',
  },
  {
    name: 'Marketplace Title & Search SEO Optimizer',
    slug: 'facebook-marketplace-title-optimizer',
    shortDescription: 'Free listing title generator and character length optimizer to maximize search impression visibility within Facebook Marketplace feeds.',
    description: 'An essential search visibility utility for Facebook Marketplace sellers. Analyzes your target listing title against marketplace character limits (100 characters maximum, 45-65 characters recommended for mobile viewport clipping), checking keyword position, attribute inclusions, and readability scores.',
    category: 'SEO & Copywriting',
    platform: 'Web Application',
    os: 'Cross-Platform (Any modern browser)',
    version: '1.1.0',
    releaseDate: 'August 2026',
    updatedDate: 'September 2026',
    developer: 'Lashary Engineering Team',
    publisher: 'Lashary Softwares',
    license: 'Free Public Web Utility',
    fileSize: '0 MB (Runs in Browser)',
    officialWebsite: 'https://lasharyfbmarketsuite.pages.dev/tools/facebook-marketplace-title-optimizer',
    downloadType: 'WEB_BASED',
    downloadURL: '/tools/facebook-marketplace-title-optimizer',
    screenshots: ['/screenshots/03_listing_data.png'],
    systemRequirements: [
      'Modern web browser on desktop or mobile device'
    ],
    installationInstructions: [
      'Open directly in your web browser with zero configuration or sign-up needed.'
    ],
    changelog: [
      'v1.1.0: Added category-specific keyword density suggestions for automotive, real estate, and consumer tech.',
      'v1.0.0: Initial title length and truncation simulator release.'
    ],
    features: [
      'Real-time mobile and desktop title truncation preview',
      'Exact character and word count tracking with 100-character platform limit alert',
      'High-intent buyer keyword density analyzer',
      'Click-through rate power word suggestions',
      'Clean 1-click clipboard copy'
    ],
    limitations: [
      'Analyzes textual structure; does not directly communicate with private Facebook APIs'
    ],
    faq: [
      {
        question: 'What is the optimal title length for Facebook Marketplace?',
        answer: 'While Facebook permits up to 100 characters, titles between 45 and 65 characters perform best because they do not get truncated on mobile phone screens.'
      }
    ],
    seoTitle: 'Facebook Marketplace Title & SEO Optimizer (Free Tool) — Lashary',
    metaDescription: 'Free Facebook Marketplace listing title optimizer. Test title character length, prevent mobile truncation, and boost search visibility with high-intent keywords.',
    canonicalURL: 'https://lasharyfbmarketsuite.pages.dev/tools/facebook-marketplace-title-optimizer',
    ogImage: 'https://lasharyfbmarketsuite.pages.dev/screenshots/03_listing_data.png',
    status: 'AVAILABLE',
  },
  {
    name: 'Listing Relist Interval & Stale Inventory Planner',
    slug: 'facebook-marketplace-relisting-tool',
    shortDescription: 'Free strategic calculator to schedule safe inventory relisting cycles and avoid algorithmic duplicate listing penalties.',
    description: 'Plan and pace your inventory relisting cycles effectively. The Facebook algorithm deprioritizes listings after 7 to 14 days without interaction. This planning tool calculates safe delete-and-relist intervals, price modification increments, and unique photo sequencing schedules.',
    category: 'Inventory & Workflow',
    platform: 'Web Application',
    os: 'Cross-Platform (Any modern browser)',
    version: '1.0.0',
    releaseDate: 'September 2026',
    updatedDate: 'September 2026',
    developer: 'Lashary Engineering Team',
    publisher: 'Lashary Softwares',
    license: 'Free Public Web Utility',
    fileSize: '0 MB (Runs in Browser)',
    officialWebsite: 'https://lasharyfbmarketsuite.pages.dev/tools/facebook-marketplace-relisting-tool',
    downloadType: 'WEB_BASED',
    downloadURL: '/tools/facebook-marketplace-relisting-tool',
    screenshots: ['/screenshots/04_staged.png'],
    systemRequirements: ['Modern web browser'],
    installationInstructions: ['Access online through your browser.'],
    changelog: ['v1.0.0: Initial release of the relisting schedule calculator.'],
    features: [
      'Calculates optimal days-to-relist based on category velocity',
      'Price decay suggestions to trigger notification badges to saved buyers',
      'Visual timeline of inventory refresh waves',
      'Checklist for modifying primary photo angles to prevent visual duplicate detection'
    ],
    limitations: [
      'Planning utility; for automated batch execution across multiple accounts, use Lashary FB Market Suite desktop client.'
    ],
    faq: [
      {
        question: 'How often should I relist items on Facebook Marketplace?',
        answer: 'For competitive retail goods, renewing or relisting every 7 to 10 days refreshes algorithmic recency. For high-ticket vehicles or properties, modifying price by 2-5% every 14 days is recommended.'
      }
    ],
    seoTitle: 'Facebook Marketplace Relisting Schedule & Planner (Free) — Lashary',
    metaDescription: 'Free Facebook Marketplace relisting planner. Discover optimal refresh intervals, price adjustment tactics, and photo sequencing to boost stale listing reach.',
    canonicalURL: 'https://lasharyfbmarketsuite.pages.dev/tools/facebook-marketplace-relisting-tool',
    ogImage: 'https://lasharyfbmarketsuite.pages.dev/screenshots/04_staged.png',
    status: 'AVAILABLE',
  },
  {
    name: 'Marketplace 1:1 Aspect Ratio Image Formatter',
    slug: 'facebook-marketplace-image-generator',
    shortDescription: 'Free in-browser image cropping and formatting tool to optimize photos for Facebook Marketplace 1200x1200 square grid displays.',
    description: 'Prepare clean, professional 1:1 square photos specifically calibrated for the Facebook Marketplace search grid. Crop, center, sharpen, and add subtle high-contrast border badges to product photos without quality loss.',
    category: 'Media & Design Utilities',
    platform: 'Web Application',
    os: 'Cross-Platform (Any modern browser)',
    version: '1.0.0',
    releaseDate: 'September 2026',
    updatedDate: 'September 2026',
    developer: 'Lashary Engineering Team',
    publisher: 'Lashary Softwares',
    license: 'Free Public Web Utility',
    fileSize: '0 MB (Runs in Browser)',
    officialWebsite: 'https://lasharyfbmarketsuite.pages.dev/tools/facebook-marketplace-image-generator',
    downloadType: 'WEB_BASED',
    downloadURL: '/tools/facebook-marketplace-image-generator',
    screenshots: ['/screenshots/01_overview.png'],
    systemRequirements: ['HTML5 Canvas compatible web browser'],
    installationInstructions: ['Load online in any browser; all processing occurs locally in client memory.'],
    changelog: ['v1.0.0: Initial release of browser-based 1200x1200 image cropper.'],
    features: [
      'Zero-upload privacy: all cropping and scaling happens directly inside your browser canvas',
      'Preset 1200x1200 Facebook Marketplace resolution',
      'Optional condition and price badge overlays',
      'Batch download as compressed web-ready JPEG'
    ],
    limitations: [
      'Processes images in browser memory; for automated unique slot distribution across 500+ inventory items, use the Lashary desktop software.'
    ],
    faq: [
      {
        question: 'What is the best image size for Facebook Marketplace in 2026?',
        answer: 'The recommended image size is 1200 x 1200 pixels with a 1:1 square aspect ratio. This ensures photos display sharply without awkward letterboxing on mobile and desktop feeds.'
      }
    ],
    seoTitle: 'Facebook Marketplace 1:1 Image Formatter & Cropper (Free) — Lashary',
    metaDescription: 'Free Facebook Marketplace image optimizer and square cropper. Format photos to 1200x1200 resolution with condition badge overlays for maximum click-through rates.',
    canonicalURL: 'https://lasharyfbmarketsuite.pages.dev/tools/facebook-marketplace-image-generator',
    ogImage: 'https://lasharyfbmarketsuite.pages.dev/screenshots/01_overview.png',
    status: 'AVAILABLE',
  },
  {
    name: 'Facebook Marketplace Category & Search Tag Finder',
    slug: 'facebook-marketplace-category-tag-finder',
    shortDescription: 'Free attribute mapper to discover official category taxonomies, required form fields, and search tags for your catalog items.',
    description: 'Eliminate marketplace submission rejections caused by missing required attributes. Search our database of Facebook Marketplace categories to find exact field requirements for vehicles (Make, Model, Mileage), properties (Bedrooms, Bathrooms, Sq Ft), and consumer goods.',
    category: 'Catalog & Taxonomy',
    platform: 'Web Application',
    os: 'Cross-Platform (Any modern browser)',
    version: '1.0.0',
    releaseDate: 'September 2026',
    updatedDate: 'September 2026',
    developer: 'Lashary Engineering Team',
    publisher: 'Lashary Softwares',
    license: 'Free Public Web Utility',
    fileSize: '0 MB (Runs in Browser)',
    officialWebsite: 'https://lasharyfbmarketsuite.pages.dev/tools/facebook-marketplace-category-tag-finder',
    downloadType: 'WEB_BASED',
    downloadURL: '/tools/facebook-marketplace-category-tag-finder',
    screenshots: ['/screenshots/03_listing_data.png'],
    systemRequirements: ['Modern web browser'],
    installationInstructions: ['Access online.'],
    changelog: ['v1.0.0: Initial release with vehicle, real estate, and electronics taxonomies.'],
    features: [
      'Searchable directory of Facebook Marketplace category paths',
      'Highlights mandatory vs optional attributes per category',
      'Extracts 20 recommended high-intent search tags per product niche',
      'Export tag list with 1 click'
    ],
    limitations: ['Static taxonomy reference database updated periodically.'],
    faq: [
      {
        question: 'Why are search tags important on Facebook Marketplace?',
        answer: 'Search tags help Facebook match your listing to long-tail buyer queries that may not appear verbatim in your title, boosting organic discoverability.'
      }
    ],
    seoTitle: 'Facebook Marketplace Category & Tag Finder (Free Tool) — Lashary',
    metaDescription: 'Free Facebook Marketplace category and search tag explorer. Find required vehicle and property attribute fields and high-intent buyer tags in seconds.',
    canonicalURL: 'https://lasharyfbmarketsuite.pages.dev/tools/facebook-marketplace-category-tag-finder',
    ogImage: 'https://lasharyfbmarketsuite.pages.dev/screenshots/03_listing_data.png',
    status: 'AVAILABLE',
  }
];

export const getToolBySlug = (slug: string): ToolItem | undefined => {
  return TOOLS_DATA.find(tool => tool.slug === slug);
};
